import React, { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle, FileText, Pen, Eraser } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

export const SignaturePad = ({ onSignatureChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#0f172a";
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
    onSignatureChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const end = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange?.("");
  };

  return (
    <div className="w-full">
      <div className="relative border-2 border-dashed border-stone-300 rounded-lg bg-stone-50 overflow-hidden" style={{ height: 200 }}>
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center text-stone-400 pointer-events-none">
            <div className="text-center">
              <Pen className="mx-auto mb-2" size={32} />
              <p className="text-sm">Hier mit Finger oder Maus unterschreiben</p>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-2">
        <p className="text-xs text-stone-500">Bitte unterschreiben Sie wie auf einem Papierdokument.</p>
        <Button variant="ghost" size="sm" onClick={clear} disabled={!hasSignature} data-testid="clear-signature-btn">
          <Eraser size={14} className="mr-1" /> Leeren
        </Button>
      </div>
    </div>
  );
};

export default function SignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [rechnungPerEmail, setRechnungPerEmail] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/sign/${token}`);
        if (res.data.status === "already_signed") {
          setSigned(true);
          setData(res.data);
        } else {
          setData(res.data);
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Link nicht gültig oder abgelaufen");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async () => {
    if (!signature) {
      toast.error("Bitte unterschreiben Sie zuerst.");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Bitte bestätigen Sie die Erklärung.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/sign/${token}/submit`, { signature, ...(data.rechnung_email_frage && rechnungPerEmail !== null ? { rechnung_per_email: rechnungPerEmail } : {}) });
      setSigned(true);
      setData({ ...data, anhaenge_count: res.data.anhaenge_count, anhaenge: res.data.anhaenge });
      toast.success("Vielen Dank — Ihre Unterschrift wurde übermittelt!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Übermitteln");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="animate-spin text-stone-400" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center">
            <AlertCircle className="mx-auto text-red-500 mb-3" size={48} />
            <h2 className="font-bold text-lg text-stone-900 mb-2">Link nicht verfügbar</h2>
            <p className="text-stone-600">{error}</p>
            <p className="text-xs text-stone-500 mt-4">Bei Fragen wenden Sie sich an info@dickies-helden.de</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50 p-4">
        <Card className="max-w-md w-full border-emerald-200">
          <CardContent className="py-10 text-center">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-4" size={64} />
            <h2 className="font-bold text-2xl text-stone-900 mb-2">Vielen Dank!</h2>
            <p className="text-stone-600 mb-4">
              Ihre Unterschrift wurde erfolgreich übermittelt. Die Unterlagen wurden automatisch an unsere Schulungsleitung weitergeleitet.
            </p>
            {data?.anhaenge && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-left text-sm">
                <p className="font-semibold text-emerald-900 mb-2">Unterschriebene Dokumente:</p>
                <ul className="space-y-1">
                  {data.anhaenge.map((a, i) => (
                    <li key={i} className="flex items-center gap-2 text-emerald-800">
                      <CheckCircle2 size={14} /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-stone-500 mt-6">Dickies-Campus GmbH</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            DICKIES-CAMPUS
          </h1>
          <p className="text-stone-500 text-sm">Pflegebildungszentrum</p>
        </div>

        <Card className="bg-white shadow-sm">
          <CardContent className="py-6">
            <h2 className="text-xl font-bold text-stone-900 mb-2">
              Hallo {data.teilnehmer.vorname},
            </h2>
            <p className="text-stone-600 mb-6">
              Bitte unterschreiben Sie folgende Unterlagen. Die Unterschrift gilt rechtlich gemäß eIDAS-Verordnung als „einfache elektronische Signatur".
            </p>

            {data.custom_message && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-6 text-sm text-blue-900">
                {data.custom_message}
              </div>
            )}

            <div className="space-y-3 mb-6">
              {data.dokumente.map((d) => (
                <a
                  key={d.key}
                  href={`${API}/sign/${token}/preview/${d.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                  data-testid={`preview-doc-${d.key}`}
                >
                  <div className="w-10 h-10 bg-orange-100 rounded flex items-center justify-center text-orange-700 flex-shrink-0">
                    <FileText size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-900">{d.label}</div>
                    <div className="text-xs text-stone-500">{d.code} · Tippen zum Ansehen</div>
                  </div>
                  <span className="text-xs text-blue-600 hover:underline">Ansehen →</span>
                </a>
              ))}
            </div>

            <div className="border-t border-stone-200 pt-6">
              <h3 className="font-semibold text-stone-900 mb-3">Ihre Unterschrift</h3>
              <SignaturePad onSignatureChange={setSignature} />
            </div>

            {data.rechnung_email_frage && (
              <div className="mt-6 p-4 bg-white border border-stone-200 rounded-lg" data-testid="rechnung-email-frage">
                <p className="text-sm font-semibold text-stone-900 mb-2">Rechnungen per E-Mail? <span className="font-normal text-stone-500">(freiwillig)</span></p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rechnungPerEmail === true}
                    onChange={(e) => setRechnungPerEmail(e.target.checked ? true : null)}
                    className="mt-1 w-4 h-4 accent-orange-500"
                    data-testid="rechnung-email-checkbox"
                  />
                  <span className="text-sm text-stone-700">{data.rechnung_email_text} <span className="text-stone-500">(E-Mail: {data.email})</span></span>
                </label>
                <p className="text-xs text-stone-500 mt-2">Ohne Häkchen schicken wir dir Rechnungen per Post.</p>
              </div>
            )}

            <div className="mt-6 p-4 bg-stone-50 border border-stone-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-orange-500"
                />
                <span className="text-sm text-stone-700">
                  Ich bestätige, dass ich die {data.dokumente.length === 1 ? 'oben aufgeführte Unterlage' : `${data.dokumente.length} oben aufgeführten Unterlagen`} gelesen habe und unterschreibe diese hiermit elektronisch verbindlich. Mir ist bewusst, dass meine IP-Adresse, der Zeitpunkt und mein Browser zur Beweissicherung gespeichert werden (eIDAS Art. 25).
                </span>
              </label>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleSubmit}
                disabled={!signature || !acceptedTerms || submitting}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-6 text-base font-medium"
                data-testid="submit-signature-btn"
              >
                {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : <Pen size={18} className="mr-2" />}
                {submitting ? 'Wird übermittelt...' : 'Verbindlich unterschreiben'}
              </Button>
            </div>

            <p className="text-xs text-stone-400 mt-6 text-center">
              Gültig bis: {new Date(data.expires_at).toLocaleDateString('de-DE')} · Schutz Ihrer Daten gemäß DSGVO
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-stone-400 mt-6">
          © Dickies-Campus GmbH · Bei Fragen: info@dickies-helden.de
        </p>
      </div>
    </div>
  );
}
